/**
 * ProposalPdfPreviewModal - Visualizador interno do PDF da proposta
 *
 * Exibe o PDF gerado (bucket privado `contratos`) dentro do sistema, usando
 * o visualizador nativo do navegador via <iframe> alimentado por uma signed
 * URL temporária. Evita que o usuário precise baixar o arquivo para conferir.
 *
 * Estados tratados:
 *  - carregando o link assinado;
 *  - PDF ainda não gerado / com falha (oferece gerar na hora);
 *  - erro ao criar o link (com opção de tentar novamente).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (preview interno do PDF)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, ExternalLink, FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProposalPdf } from '@/hooks/useDealProposals';

interface ProposalPdfPreviewModalProps {
  /** Modal aberto? */
  open: boolean;
  /** Handler de fechamento */
  onClose: () => void;
  /** ID da proposta (necessário para gerar/regerar) */
  proposalId: string | null;
  /** Título exibido no cabeçalho */
  title?: string | null;
  /** Caminho do PDF no Storage (null quando ainda não gerado) */
  storagePath?: string | null;
}

export function ProposalPdfPreviewModal({
  open,
  onClose,
  proposalId,
  title,
  storagePath,
}: ProposalPdfPreviewModalProps) {
  const { generatePdf, getPdfSignedUrl, openPdf } = useProposalPdf();

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Último caminho já solicitado — evita refazer a signed URL em loop */
  const requestedPathRef = useRef<string | null>(null);

  /** Busca (ou renova) a signed URL do arquivo */
  const loadUrl = useCallback(
    async (path: string) => {
      setLoading(true);
      setError(null);
      const url = await getPdfSignedUrl(path);
      if (!url) {
        setError('Não foi possível carregar o PDF.');
        setSignedUrl(null);
      } else {
        setSignedUrl(url);
      }
      setLoading(false);
    },
    [getPdfSignedUrl],
  );

  // Carrega o link sempre que o modal abre com um caminho válido.
  // Dependências propositalmente limitadas a [open, storagePath]: incluir
  // `loadUrl` reintroduz o loop caso alguma dependência volte a oscilar.
  useEffect(() => {
    if (!open) {
      requestedPathRef.current = null;
      setSignedUrl(null);
      setError(null);
      return;
    }
    if (!storagePath) {
      requestedPathRef.current = null;
      setSignedUrl(null);
      return;
    }
    // Guarda: já buscamos este caminho nesta abertura do modal
    if (requestedPathRef.current === storagePath) return;
    requestedPathRef.current = storagePath;
    void loadUrl(storagePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storagePath]);


  /** Gera o PDF na hora e recarrega o preview */
  const handleGenerate = async () => {
    if (!proposalId) return;
    const result = await generatePdf.mutateAsync(proposalId);
    if (result?.storage_path) {
      await loadUrl(result.storage_path);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[92dvh] p-0 gap-0 flex flex-col sm:max-w-[92vw]">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base pr-8">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{title || 'Proposta'}</span>

            <div className="ml-auto flex items-center gap-1.5">
              {signedUrl && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => window.open(signedUrl, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Nova aba
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => openPdf(storagePath ?? null)}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Baixar
                  </Button>
                </>
              )}
              {proposalId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={generatePdf.isPending}
                  onClick={handleGenerate}
                  title="Gerar novamente o PDF"
                >
                  {generatePdf.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  )}
                  Regerar
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Área do visualizador */}
        <div className="flex-1 min-h-0 bg-muted/30">
          {loading || generatePdf.isPending ? (
            <div className="h-full flex flex-col items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                {generatePdf.isPending ? 'Gerando o PDF...' : 'Carregando o PDF...'}
              </p>
            </div>
          ) : signedUrl ? (
            <iframe
              key={signedUrl}
              src={signedUrl}
              title="Pré-visualização da proposta"
              className="w-full h-full border-0"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                {error || 'O PDF desta proposta ainda não foi gerado.'}
              </p>
              {proposalId && (
                <Button size="sm" onClick={handleGenerate} disabled={generatePdf.isPending}>
                  {error && storagePath ? 'Tentar novamente' : 'Gerar PDF'}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
